// src/utils/myCoastAPI.js
// MyCoast数据获取工具 - 清理版本，只使用真实数据

/**
 * 计算两个地理坐标之间的距离（哈弗辛公式）
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // 地球半径（千米）
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * CORS代理配置 - 重新排序，把工作的放在前面
 */
const CORS_PROXIES = {
  primary: 'https://api.codetabs.com/v1/proxy?quest=',
  backup1: 'https://api.allorigins.win/get?url=',
  backup2: 'https://cors-anywhere.herokuapp.com/',
};

/**
 * 地址缓存 - 避免重复的地理编码请求
 */
const addressCache = new Map();

/**
 * 生成坐标的缓存键
 */
const getCacheKey = (lat, lon) => {
  return `${lat.toFixed(3)},${lon.toFixed(3)}`;
};

/**
 * 使用CORS代理发起请求
 */
const fetchWithProxy = async (targetUrl, proxyType = 'primary') => {
  const proxyUrl = CORS_PROXIES[proxyType];
  
  if (proxyType === 'backup1') {
    // AllOrigins返回JSON格式，需要特殊处理
    const response = await fetch(`${proxyUrl}${encodeURIComponent(targetUrl)}`);
    const data = await response.json();
    
    if (data.contents) {
      return {
        ok: true,
        json: () => Promise.resolve(JSON.parse(data.contents)),
        text: () => Promise.resolve(data.contents)
      };
    } else {
      throw new Error('AllOrigins proxy failed');
    }
  } else {
    return fetch(`${proxyUrl}${targetUrl}`, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:109.0) Gecko/20100101 Firefox/116.0',
        'Accept': 'application/json'
      }
    });
  }
};

/**
 * 带重试机制的请求函数
 */
const fetchWithRetry = async (targetUrl) => {
  const proxies = ['primary', 'backup1', 'backup2'];
  let lastError;

  for (const proxyType of proxies) {
    try {
      console.log(`📡 Trying ${proxyType} proxy for MyCoast API...`);
      const response = await fetchWithProxy(targetUrl, proxyType);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`✅ ${proxyType} proxy successful`);
      return data;
      
    } catch (error) {
      console.warn(`❌ ${proxyType} proxy failed:`, error.message);
      lastError = error;
      
      // 在重试之间添加延迟
      if (proxyType !== 'backup2') {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  throw new Error(`All CORS proxies failed. Last error: ${lastError?.message}`);
};

/**
 * 智能地址生成 - 基于坐标生成合理的地址
 */
const generateSmartAddress = (lat, lon) => {
  // Brooklyn和Queens的大概区域
  const neighborhoods = {
    brooklyn: [
      { name: "Brooklyn Heights", lat: 40.6962, lon: -73.9441, radius: 0.02 },
      { name: "DUMBO", lat: 40.7033, lon: -73.9878, radius: 0.015 },
      { name: "Park Slope", lat: 40.6736, lon: -73.9566, radius: 0.02 },
      { name: "Williamsburg", lat: 40.7081, lon: -73.9571, radius: 0.025 },
      { name: "Red Hook", lat: 40.6743, lon: -74.0057, radius: 0.02 },
      { name: "Canarsie", lat: 40.6387, lon: -73.9018, radius: 0.03 },
      { name: "Coney Island", lat: 40.5755, lon: -73.9707, radius: 0.025 },
      { name: "Bay Ridge", lat: 40.6259, lon: -74.0287, radius: 0.02 }
    ],
    queens: [
      { name: "Long Island City", lat: 40.7505, lon: -73.9934, radius: 0.02 },
      { name: "Astoria", lat: 40.7648, lon: -73.9242, radius: 0.025 },
      { name: "Flushing", lat: 40.7631, lon: -73.8370, radius: 0.03 },
      { name: "Queens Village", lat: 40.7287, lon: -73.7370, radius: 0.02 },
      { name: "Howard Beach", lat: 40.6565, lon: -73.8424, radius: 0.02 },
      { name: "Rockaway Beach", lat: 40.5834, lon: -73.8154, radius: 0.03 }
    ]
  };

  // 查找最近的已知区域
  let closestNeighborhood = null;
  let minDistance = Infinity;

  [...neighborhoods.brooklyn, ...neighborhoods.queens].forEach(area => {
    const distance = calculateDistance(lat, lon, area.lat, area.lon);
    if (distance < area.radius && distance < minDistance) {
      minDistance = distance;
      closestNeighborhood = area.name;
    }
  });

  if (closestNeighborhood) {
    const borough = neighborhoods.brooklyn.some(area => area.name === closestNeighborhood) 
      ? 'Brooklyn' : 'Queens';
    return `${closestNeighborhood}, ${borough}`;
  }

  // 如果找不到已知区域，根据坐标范围判断区
  if (lat >= 40.570 && lat <= 40.739 && lon >= -74.042 && lon <= -73.833) {
    return `Brooklyn, NY`;
  } else if (lat >= 40.541 && lat <= 40.800 && lon >= -73.962 && lon <= -73.700) {
    return `Queens, NY`;
  }

  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
};

/**
 * 反向地理编码 - 优化版本，使用缓存和智能回退
 */
const reverseGeocode = async (lat, lon) => {
  const cacheKey = getCacheKey(lat, lon);
  
  // 检查缓存
  if (addressCache.has(cacheKey)) {
    return addressCache.get(cacheKey);
  }

  try {
    // 只对少数图片进行真实的地理编码
    const shouldGeocode = Math.random() < 0.3; // 30%的概率进行真实地理编码
    
    if (shouldGeocode) {
      const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=16&addressdetails=1`;
      
      // 只使用最可靠的代理
      const response = await fetchWithProxy(nominatimUrl, 'primary');
      const data = await response.json();
      
      if (data && data.display_name) {
        const parts = data.display_name.split(',');
        const address = parts.slice(0, 2).join(', ').trim();
        addressCache.set(cacheKey, address);
        return address;
      }
    }
    
    // 使用智能地址生成作为默认方案
    const smartAddress = generateSmartAddress(lat, lon);
    addressCache.set(cacheKey, smartAddress);
    return smartAddress;
    
  } catch (error) {
    console.warn('Reverse geocoding failed, using smart address:', error.message);
    const smartAddress = generateSmartAddress(lat, lon);
    addressCache.set(cacheKey, smartAddress);
    return smartAddress;
  }
};

/**
 * 获取MyCoast洪水图片数据 - 清理版本，失败时返回空数组
 */
export const fetchMyCoastData = async (maxPages = 3) => {
  const allData = [];
  let currentPage = 1;
  let hasMoreData = true;

  try {
    console.log('📸 Attempting to fetch real MyCoast data...');
    
    while (hasMoreData && currentPage <= maxPages) {
      const targetUrl = `https://mycoast.org/blueurchin-js/arcgis/can-ny.php?page=${currentPage}`;
      
      try {
        console.log(`📸 Fetching MyCoast page ${currentPage}...`);
        const data = await fetchWithRetry(targetUrl);
        
        if (!data.features || data.features.length === 0) {
          console.log(`📸 No more data on page ${currentPage}`);
          hasMoreData = false;
          break;
        }

        // 处理数据格式，只保留有效的图片数据
        const processedData = data.features
          .filter(feature => {
            return feature.geometry && 
                   feature.geometry.coordinates && 
                   feature.properties && 
                   feature.properties.picUrl &&
                   feature.properties.picUrl.trim() !== '';
          })
          .map(feature => {
            const lat = feature.geometry.coordinates[1];
            const lon = feature.geometry.coordinates[0];
            
            return {
              id: feature.properties.id || `mycoast_${currentPage}_${Math.random()}`,
              coordinates: feature.geometry.coordinates,
              lat: lat,
              lon: lon,
              picUrl: feature.properties.picUrl,
              comment: feature.properties.comment || '',
              photoDate: feature.properties.photo_date || '',
              photoTime: feature.properties.photo_time || '',
              datetime: feature.properties.photo_date && feature.properties.photo_time ? 
                new Date(`${feature.properties.photo_date} ${feature.properties.photo_time}`) : 
                new Date(),
              address: generateSmartAddress(lat, lon)
            };
          });

        allData.push(...processedData);
        
        if (data.features.length < 50) {
          hasMoreData = false;
        }

        currentPage++;
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (pageError) {
        console.error(`❌ Failed to fetch page ${currentPage}:`, pageError);
        break;
      }
    }

    if (allData.length > 0) {
      console.log(`✅ Successfully loaded ${allData.length} real MyCoast images from ${currentPage - 1} pages`);
      return allData;
    } else {
      console.log('📸 No MyCoast images found');
      return [];
    }

  } catch (error) {
    console.error('❌ Error fetching MyCoast data:', error);
    console.log('📸 Returning empty array - no mock data');
    return []; // 🔥 关键修改：失败时返回空数组，不使用模拟数据
  }
};

/**
 * 根据传感器位置筛选附近的MyCoast图片 - 清理版本
 */
export const findNearbyImages = async (myCoastData, sensorCoordinates, radiusKm = 2, maxResults = 6) => {
  if (!myCoastData || !sensorCoordinates || sensorCoordinates.length !== 2 || myCoastData.length === 0) {
    console.log('📸 No MyCoast data available for nearby search');
    return [];
  }

  const [sensorLat, sensorLon] = sensorCoordinates;
  
  // 计算距离并筛选
  const nearbyImages = myCoastData
    .map(image => ({
      ...image,
      distance: calculateDistance(sensorLat, sensorLon, image.lat, image.lon)
    }))
    .filter(image => image.distance <= radiusKm)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxResults);

  // 为没有地址的图片生成地址
  const imagesWithAddresses = nearbyImages.map(image => {
    if (!image.address || image.address.includes('undefined')) {
      image.address = generateSmartAddress(image.lat, image.lon);
    }
    return image;
  });

  console.log(`📸 Found ${imagesWithAddresses.length} real MyCoast images within ${radiusKm}km`);
  return imagesWithAddresses;
};

/**
 * 格式化MyCoast图片的时间信息
 */
export const formatMyCoastTime = (datetime) => {
  if (!datetime || !(datetime instanceof Date)) {
    return 'Unknown time';
  }
  
  return datetime.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * 验证MyCoast图片URL是否有效
 */
export const validateImageUrl = async (url) => {
  try {
    const response = await fetchWithProxy(url, 'primary');
    return response.ok && response.headers.get('content-type')?.startsWith('image/');
  } catch {
    return false;
  }
};

export default {
  fetchMyCoastData,
  findNearbyImages,
  formatMyCoastTime,
  validateImageUrl,
  calculateDistance
};